import { Box } from "flicket-ui";
import { useAtom } from "jotai";
import { useRouter } from "next/router";
import { ReactNode, useContext } from "react";
import { Footer, Menu, PageContainer } from "~components";
import HeaderAlertBanner from "~components/common/common.HeaderAlertBanner";
import Image from "~components/common/Image";
import { ReservationList } from "~components/reservation/non-seated";
import {
  ImportantNotice,
  R18Notice,
} from "~components/reservation/non-seated/non-seated.components";
import { useFilterVisibleZonesAndTickets } from "~components/reservation/non-seated/useFilterVisibleZonesAndTickets.hook";
import { useGetTicketsSoldForUser } from "~components/reservation/non-seated/useGetTicketsSoldForUser.hook";
import { UserContext } from "~context";
import { getRegistrationFormKeyAndValues } from "~features/events/components/RegistrationModalContent";
import { ExperienceSalesFooter } from "~features/nonseated-reservation/components/ExperienceSalesFooter/ExperienceSalesFooter";
import { isPrechargeFlowAtom } from "~features/nonseated-reservation/isPrechargeFlowAtom";
import {
  INormalisedData,
  IReleaseTicket,
  IReleaseZone,
} from "~features/nonseated-reservation/nonseated-reservation.types";
import { useNonSeatedReservationStore } from "~features/nonseated-reservation/store/non-seated-reservation-store";
import { ReleaseType } from "~graphql/sdk";
import { CreateOrderFn, useOrganization } from "~hooks";
import { removeUnsyncedQueryParams } from "~lib/state-stores/appStore/action.removeUnsyncedQueryParams";
import { Banner } from "./Banner";
import { CONTENT_MAX_WIDTH } from "./non-seated.constants";
import { PrechargeBanner } from "./PrechargeBanner";
import { StickyZoneTabs } from "./StickyZoneTabs";
import { useIntersectZone } from "./useIntersection.hook";
import { MemberTicketsBanner } from "./MemberTicketsBanner";

type NonSeatedProps = {
  data: INormalisedData;
  zones: IReleaseZone[];
  addons: IReleaseTicket[];
  createOrder: CreateOrderFn;
  isMembership: boolean;
  isAdmin: boolean;
  releaseId?: string;
  membershipId?: string;
  releaseNotes: string;
  allowPublicComp: boolean;
  isResaleRelease: boolean;
  releaseType: ReleaseType;
  requiresAuth: boolean;
  showMemberTicketsBanner: boolean;
};

export const NonSeatedReservationPage = ({
  addons,
  data,
  zones,
  createOrder,
  isMembership = false,
  isAdmin,
  releaseId,
  membershipId,
  releaseNotes,
  allowPublicComp,
  isResaleRelease,
  releaseType,
  requiresAuth = false,
  showMemberTicketsBanner,
}: NonSeatedProps) => {
  const { hasFeature } = useOrganization();
  const router = useRouter();

  const [isPrechargeFlow] = useAtom(isPrechargeFlowAtom);

  const calendarView = hasFeature("EventCalendarHomepage");

  const { authState } = useContext(UserContext);
  const isPOSRelease = releaseType === ReleaseType.Pos;

  const selectedTickets = useNonSeatedReservationStore(
    (state) => state.selectedTicketTypes[data?.id]
  );

  const { ticketsSoldForUser } = useGetTicketsSoldForUser({
    data,
    isMembership,
    isLoggedIn: authState === "authenticated",
  });

  const { zones: visibleZonesAndTickets, addons: addonTypes } =
    useFilterVisibleZonesAndTickets({
      zones,
      addons,
      selectedTickets,
      ticketsSoldForUser,
      isPresaleRelease: releaseType === ReleaseType.Presale,
      isAdmin,
    });

  // This intersection observer is used to remove the zoneId query param
  // when the user scrolls back to the top of the page.
  const { ref: intersectionRef } = useIntersectZone(
    {
      onIntersectTop: () => {
        removeUnsyncedQueryParams(["zoneId"]);
      },
    },
    {
      threshold: [0, 1],
    }
  );

  const showStickyTabs =
    (visibleZonesAndTickets?.length ?? 0) + (addonTypes?.length ?? 0) > 1;

  const maxWidth = calendarView ? "none" : CONTENT_MAX_WIDTH;

  const removeBackgroundImage = hasFeature(
    "RemoveBackgroundImageFromTicketPage"
  );

  if (isPrechargeFlow) {
    const [, values] = getRegistrationFormKeyAndValues(releaseId);

    if (!values?.email) {
      void router.push(`/events/${data?.id}`);
    }
  }

  return (
    <Box width={1} pt={{ md: calendarView ? 0 : 0 }}>
      <Box
        css={`
          z-index: 1400;
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          display: flex; /* Ensure it uses flex layout */
          flex-wrap: wrap; /* Allow child elements to wrap */
          justify-content: center; /* Optional: center the text horizontally */
          align-items: center; /* Optional: center the text vertically */
        `}
        flexWrap="wrap"
      >
        <HeaderAlertBanner />
      </Box>
      {calendarView && <Menu sticky={false} showBanner={false} />}
      <Wrapper data={data} removeBackgroundImage={removeBackgroundImage}>
        <Box ref={intersectionRef}>
          {!isPOSRelease && <Box pt={{ sm: 3 }} />}

          {!isPOSRelease && removeBackgroundImage && (
            <Box
              width={1}
              px={{ _: 0, sm: 2 }}
              maxW={{ sm: maxWidth }}
              bg="white"
              margin={"auto" as any}
              mb={4}
            >
              <Box position="relative">
                {data.banner && (
                  <Image
                    image={data.banner}
                    alt={data.name}
                    width={{ _: "100%" }}
                    borderRadius={{ sm: "sm" }}
                    backgroundColor="N200"
                  />
                )}
              </Box>
            </Box>
          )}

          <Box
            width={1}
            px={2}
            maxW={{ sm: maxWidth }}
            bg="white"
            margin={"auto" as any}
            mb={2}
            mt={isPOSRelease ? 5 : 0}
          >
            {isPrechargeFlow ? (
              <PrechargeBanner data={data} />
            ) : (
              <Banner data={data} isPOSRelease={isPOSRelease} />
            )}

            {showMemberTicketsBanner && <MemberTicketsBanner />}

            <Box>
              {data.isR18 && !isPOSRelease && <R18Notice />}
              {!isPOSRelease && (
                <>
                  {/* Event specific notes */}
                  <ImportantNotice value={data.importantNotice} />
                  {/* Release specific notes */}
                  <ImportantNotice value={releaseNotes} title={null} />
                </>
              )}
            </Box>
          </Box>
        </Box>

        {showStickyTabs && (
          <StickyZoneTabs
            zones={visibleZonesAndTickets}
            addons={addonTypes}
            isPOSRelease={isPOSRelease}
            calendarView={calendarView}
          />
        )}

        <Box
          width={1}
          px={2}
          maxW={{ sm: maxWidth }}
          bg="white"
          margin={"auto" as any}
        >
          <ReservationList
            addons={addons}
            data={data}
            zones={zones}
            createOrder={createOrder}
            isMembership={isMembership}
            releaseNotes={releaseNotes}
            isDigital={data.isDigital}
            allowPublicComp={allowPublicComp}
            isResaleRelease={isResaleRelease}
            releaseType={releaseType}
            requiresAuth={requiresAuth}
          />
        </Box>

        <ExperienceSalesFooter
          id={data.id}
          releaseId={releaseId}
          membershipId={membershipId}
          hideFooterDuringMode={["no-selection"]}
          timezone={data.timezone}
        />

        {!isPOSRelease && removeBackgroundImage && (
          <Footer narrow={true} display={{ _: "flex", md: "flex" }} />
        )}
      </Wrapper>
    </Box>
  );
};

function Wrapper({
  children,
  data,
  removeBackgroundImage,
}: {
  children: ReactNode;
  data: INormalisedData;
  removeBackgroundImage: boolean;
}) {
  if (removeBackgroundImage) return <>{children}</>;
  return (
    <PageContainer
      backgroundImage={data.backgroundImage}
      backgroundImageFullPage={data.backgroundImageFullPage}
      bannerImage={data.banner}
      titleText={data.name}
      footer={<Footer />}
      containerProps={{
        position: "relative",
        pl: 0,
        pr: 0,
        maxWidth: { md: "1120px" },
      }}
    >
      {children}
    </PageContainer>
  );
}
